import { createInstance } from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import Backend from 'i18next-http-backend';
import { initReactI18next } from 'react-i18next';

import { capitalizeString } from '~utils/stringUtils';

import { APP_VERSION } from './version';

const supportedLngs = ['en', 'nl', 'de'] as const;
const supportedLngsSet = new Set<string>(supportedLngs);

const appendQueryParam = (url: string, key: string, value: string) => {
	if (!value) {
		return url;
	}
	const separator = url.includes('?') ? '&' : '?';
	return `${url}${separator}${encodeURIComponent(key)}=${encodeURIComponent(value)}`;
};

export const getLanguageFromCulture = (culture?: string): string => {
	const languageCode = culture?.split('-')[0];
	const normalized = languageCode?.toLowerCase();
	if (!normalized) {
		return 'en';
	}
	return supportedLngsSet.has(normalized) ? normalized : 'en';
};

const i18n = createInstance();
i18n
	.use(initReactI18next)
	.use(Backend)
	.use(LanguageDetector)
	.init({
		showSupportNotice: false,
		backend: {
			loadPath: (lngs: unknown[], namespaces: unknown[]) => {
				const lng = Array.isArray(lngs) ? lngs[0] : lngs;
				const ns = Array.isArray(namespaces) ? namespaces[0] : namespaces;
				const url = `/locales/${lng}/${ns}.json`;
				return appendQueryParam(url, 'v', APP_VERSION);
			},
		},
		fallbackLng: 'en',
		supportedLngs: [...supportedLngs],
		debug: import.meta.env.NODE_ENV === 'development',
		ns: [
			'actions',
			'auth',
			'date',
			'entities',
			'errors',
			'nav',
			'status',
			'system',
			'table',
			'toast',
			'validation',
		],
		keySeparator: '.',
	});

i18n.services.formatter?.add('capitalize', (value: string, _, options) => {
	return options.formatParams?.ignore ? value : capitalizeString(value);
});
i18n.services.formatter?.add('lowercase', (value: string, _, options) => {
	return options.formatParams?.ignore ? value : value.toLocaleLowerCase();
});

export default i18n;
